/**
 * The app shell: session gate, navigation, range selection, view mounting.
 *
 * Three session states, each honest about itself:
 * - `loading`  — we have not heard from the server yet.
 * - `anonymous` — no session; show the login door, not an error.
 * - a `Me`     — mount the app.
 */

import { lazy, Suspense, useEffect, useState } from 'react'
import { AutoRefreshControl } from './AutoRefreshControl'
import { DEFAULT_INTERVAL_MS, useAutoRefresh } from './useAutoRefresh'
import { api } from '../api/client'
import type { BudgetEvaluation, Me, RangeParams } from '../api/types'
import { NAV, useRoute } from './router'
import { RangePicker } from './RangePicker'
import { ROUTE_VIEW_LOADERS } from './lazyViews'

type Session = { state: 'loading' } | { state: 'anonymous' } | { state: 'in'; me: Me }

const ROUTED_VIEWS = {
  dashboard: lazy(ROUTE_VIEW_LOADERS.dashboard),
  models: lazy(ROUTE_VIEW_LOADERS.models),
  tools: lazy(ROUTE_VIEW_LOADERS.tools),
  hours: lazy(ROUTE_VIEW_LOADERS.hours),
  sessions: lazy(ROUTE_VIEW_LOADERS.sessions),
  projects: lazy(ROUTE_VIEW_LOADERS.projects),
  quotas: lazy(ROUTE_VIEW_LOADERS.quotas),
  people: lazy(ROUTE_VIEW_LOADERS.people),
  events: lazy(ROUTE_VIEW_LOADERS.events),
  reconciliation: lazy(ROUTE_VIEW_LOADERS.reconciliation),
  account: lazy(ROUTE_VIEW_LOADERS.account),
  sources: lazy(ROUTE_VIEW_LOADERS.sources),
  'data-quality': lazy(ROUTE_VIEW_LOADERS['data-quality']),
  budgets: lazy(ROUTE_VIEW_LOADERS.budgets),
} as const

export function App() {
  const [session, setSession] = useState<Session>({ state: 'loading' })
  const { route, navigate } = useRoute()
  const [range, setRange] = useState<RangeParams>({})
  const [intervalMs, setIntervalMs] = useState<number>(DEFAULT_INTERVAL_MS)
  const refresh = useAutoRefresh(intervalMs)
  // True only when the server mounts the dev login (local, no Google). The probe
  // is side-effect-free; a 404 in production leaves this false.
  const [devLogin, setDevLogin] = useState(false)
  const [budgetAlerts, setBudgetAlerts] = useState<readonly BudgetEvaluation[]>([])

  useEffect(() => {
    void api.me().then((result) => {
      if (result.kind === 'ok') setSession({ state: 'in', me: result.data })
      else setSession({ state: 'anonymous' })
    })
    void fetch('/api/v1/auth/dev-login/available', { cache: 'no-store' })
      .then((r) => setDevLogin(r.ok))
      .catch(() => setDevLogin(false))
  }, [])

  useEffect(() => {
    if (session.state !== 'in' || session.me.role !== 'admin') {
      setBudgetAlerts([])
      return
    }
    let stale = false
    void api.budgets().then((result) => {
      if (stale) return
      setBudgetAlerts(result.kind === 'ok'
        ? result.data.filter((budget) => budget.status === 'threshold_breached' || budget.status === 'exceeded')
        : [])
    })
    return () => { stale = true }
  }, [session, refresh.tick])

  if (session.state === 'loading') {
    return <main className="gate">Checking session…</main>
  }

  if (session.state === 'anonymous') {
    return (
      <main className="gate">
        <h1>SKYPhoenix LLM Usage</h1>
        {devLogin ? (
          <>
            <p>Local development — Google sign-in is not configured on this server.</p>
            <button
              type="button"
              className="primary"
              onClick={() => {
                window.location.href = '/api/v1/auth/dev-login'
              }}
            >
              Sign in (local dev)
            </button>
          </>
        ) : (
          <>
            <p>Sign in with your @skyphoenix-it.com Google Workspace account.</p>
            <button
              type="button"
              className="primary"
              onClick={() => api.login(window.location.pathname)}
            >
              Sign in with Google
            </button>
          </>
        )}
      </main>
    )
  }

  const { me } = session
  const visibleNav = NAV.filter((entry) => entry.roles === undefined || entry.roles.includes(me.role))
  const activeEntry = NAV.find((entry) => entry.route.view === route.view)
  const activeGroup = activeEntry?.group ?? 'Overview'
  const activeLabel = activeEntry?.label ?? 'Executive overview'
  const groups = ['Overview', 'Analysis', 'Operations'] as const
  const ActiveView = ROUTED_VIEWS[route.view]

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <aside className="sidebar">
        <div className="brand-block">
          <span className="brand-mark" aria-hidden="true">S</span>
          <span><strong>SKYPhoenix</strong><small>LLM intelligence</small></span>
        </div>
        <nav aria-label="Main">
          {groups.map((group) => {
            const entries = visibleNav.filter((entry) => entry.group === group)
            if (entries.length === 0) return null
            return (
              <div className="nav-group" key={group}>
                <span className="nav-group__label">{group}</span>
                {entries.map((entry) => (
                  <a
                    key={entry.path}
                    href={entry.path}
                    aria-current={entry.route.view === route.view ? 'page' : undefined}
                    onClick={(event) => {
                      event.preventDefault()
                      navigate(entry.path)
                    }}
                  >
                    {entry.shortLabel}
                  </a>
                ))}
              </div>
            )
          })}
        </nav>
        <div className="sidebar-user">
          <span className="user-avatar" aria-hidden="true">{me.email.slice(0, 1).toUpperCase()}</span>
          <span><strong>{me.email}</strong><small>{me.role}</small></span>
        </div>
      </aside>

      <div className="workspace">
        <header className="topbar">
          <div className="topbar-title">
            <span className="eyebrow">{activeGroup}</span>
            <strong>{activeLabel}</strong>
          </div>
          <div className="topbar-controls">
            <RangePicker value={range} onChange={setRange} />
            <AutoRefreshControl
              intervalMs={intervalMs}
              onIntervalChange={setIntervalMs}
              lastRefreshAt={refresh.lastRefreshAt}
              paused={refresh.paused}
              onRefreshNow={refresh.refreshNow}
            />
          </div>
        </header>

        {budgetAlerts.length > 0 && route.view !== 'budgets' && (
          <a
            className="global-budget-alert"
            href="/budgets"
            onClick={(event) => { event.preventDefault(); navigate('/budgets') }}
          >
            <strong>{budgetAlerts.length} budget alert{budgetAlerts.length === 1 ? '' : 's'}</strong>
            <span>Provider-billed cash crossed a configured threshold. Review now.</span>
          </a>
        )}

        <main className="content" id="main-content">
          <Suspense
            fallback={(
              <div className="route-loading" role="status" aria-live="polite">
                <span className="route-loading__pulse" aria-hidden="true" />
                Loading {activeLabel.toLowerCase()}…
              </div>
            )}
          >
            <ActiveView
              range={range}
              me={me}
              refreshTick={refresh.tick}
              onRefreshNow={refresh.refreshNow}
            />
          </Suspense>
        </main>
      </div>
    </div>
  )
}
